// Avatar config + photo sampling. Ported from the Claude Design dot-head prototype,
// made config-driven (no window globals) and TypeScript.

import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';

export type AvatarState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'forming';

export interface RegionColors {
  skin: string; hair: string; beard: string;
  iris: string; lips: string; noseTint: string; brows: string;
}
export interface RegionDensity {
  skin: number; hair: number; beard: number;
  eyes: number; brows: number; nose: number; mouth: number; halo: number;
}
export interface ColorOpts { iris: number; lips: number; nose: number; brow: number }
export interface StateScalars { idle: number; listening: number; thinking: number; speaking: number }

// A normalized (0..1) ellipse over the portrait, used to mark facial regions.
export interface Ellipse { x: number; y: number; rx: number; ry: number }
// Detected (or hand-placed) facial regions that override the fixed heuristic
// ellipses when present — populated by face-landmark detection.
export interface FaceRegions {
  eyeL: Ellipse; eyeR: Ellipse; browL: Ellipse; browR: Ellipse;
  nose: Ellipse; mouth: Ellipse; beardL: Ellipse; beardR: Ellipse;
}

export interface AvatarConfig {
  sampleW: number;        // 120–320 sampling resolution
  fidelity: number;       // 0..1 — higher = closer to photo (less drift, more focus)
  dotSize: number;        // 0.4..2 global dot size multiplier
  teethVis: number;       // 0..1.5
  smile: number;          // -1..1
  eyeOpen: number;        // 0..1
  stageBg: string;        // avatar backdrop: hex, 'transparent', or 'auto' (contrast the theme)
  haloColor: string;      // rgba/hex for halo particles
  regionColors: RegionColors;
  regionDensity: RegionDensity;
  colorOpts: ColorOpts;   // per-feature tint intensity
  stateSpeeds: StateScalars;  // motion amplitude per state
  stateFreqs: StateScalars;   // motion frequency per state
  faceRegions?: FaceRegions;  // optional landmark-detected region geometry
  photoColorMix: number;  // 0 = stylized region palette, 1 = real photo colors
  cropToHead?: boolean;   // keep only the head, drop the photo background
}

export const DEFAULT_AVATAR_CONFIG: AvatarConfig = {
  sampleW: 300,
  fidelity: 0.78,
  dotSize: 1.0,
  teethVis: 1.0,
  smile: 0,
  eyeOpen: 1,
  stageBg: 'auto',
  haloColor: 'rgba(255, 200, 140, 0.7)',
  regionColors: {
    skin: '#ffc882', hair: '#cdd3df', beard: '#b9c0cc',
    iris: '#6ea0e6', lips: '#ff8c8c', noseTint: '#ffaa82', brows: '#b4825a',
  },
  regionDensity: { skin: 1, hair: 1, beard: 1, eyes: 1, brows: 1, nose: 1, mouth: 1, halo: 1 },
  colorOpts: { iris: 1, lips: 1, nose: 1, brow: 1 },
  stateSpeeds: { idle: 1, listening: 1, thinking: 1, speaking: 1 },
  stateFreqs: { idle: 1, listening: 1, thinking: 1, speaking: 1 },
  photoColorMix: 0.7, // real photos resemble the original by default
  cropToHead: false,
};

// The built-in default avatar: the golden particle face from the intro video.
// DotHead samples this when no photo is selected. It's tuned all-gold (feature
// tints off, gold region colors) so the dot cloud reproduces the crystal face.
export const DEFAULT_FACE_IMAGE = '/avatar-default.png';
export const DEFAULT_FACE_CONFIG: AvatarConfig = {
  ...DEFAULT_AVATAR_CONFIG,
  sampleW: 512,         // near-native (source is 600px) so contour lines stay crisp
  fidelity: 0.92,       // sharp, minimal drift → faithful to the source frame
  dotSize: 0.8,         // fine dots like the original
  teethVis: 0,
  haloColor: 'rgba(255, 190, 90, 0.5)',
  regionColors: {
    skin: '#ffc24d', hair: '#f0a830', beard: '#e8a23c',
    iris: '#ffc24d', lips: '#ffc24d', noseTint: '#ffc24d', brows: '#f0a830',
  },
  colorOpts: { iris: 0, lips: 0, nose: 0, brow: 0 }, // no feature tints
  // The source frame IS golden, so take its real per-pixel luminosity: the bright
  // contours glow and the fill stays dark, reproducing the reference faithfully.
  photoColorMix: 0.62,
  stateSpeeds: { idle: 0.6, listening: 0.9, thinking: 0.9, speaking: 1 }, // calm idle, like the still
};

// One-click style presets — each layers a coordinated palette (region colors +
// feature tints + halo) over the current config without touching form/motion.
export interface StylePreset { name: string; patch: Partial<AvatarConfig> }
export const STYLE_PRESETS: StylePreset[] = [
  {
    name: 'Gold',
    patch: {
      haloColor: 'rgba(255, 190, 90, 0.55)',
      regionColors: { skin: '#ffc24d', hair: '#f0a830', beard: '#e8a23c', iris: '#ffc24d', lips: '#ffc24d', noseTint: '#ffc24d', brows: '#f0a830' },
      colorOpts: { iris: 0, lips: 0, nose: 0, brow: 0 },
    },
  },
  {
    name: 'Natural',
    patch: {
      haloColor: 'rgba(255, 200, 140, 0.7)',
      regionColors: { skin: '#ffc882', hair: '#cdd3df', beard: '#b9c0cc', iris: '#6ea0e6', lips: '#ff8c8c', noseTint: '#ffaa82', brows: '#b4825a' },
      colorOpts: { iris: 1, lips: 1, nose: 1, brow: 1 },
    },
  },
  {
    name: 'Neon',
    patch: {
      haloColor: 'rgba(120, 220, 255, 0.6)',
      regionColors: { skin: '#9fd8ff', hair: '#c89bff', beard: '#a07bdf', iris: '#36e0ff', lips: '#ff5cd0', noseTint: '#7fd0ff', brows: '#c89bff' },
      colorOpts: { iris: 1.4, lips: 1.4, nose: 0.8, brow: 1.1 },
    },
  },
  {
    name: 'Ember',
    patch: {
      haloColor: 'rgba(255, 120, 60, 0.6)',
      regionColors: { skin: '#ff9a5a', hair: '#ff6a3c', beard: '#e85a30', iris: '#ffd060', lips: '#ff5a4a', noseTint: '#ff8050', brows: '#c2502a' },
      colorOpts: { iris: 1.1, lips: 1.3, nose: 1.0, brow: 1.1 },
    },
  },
  {
    name: 'Mono',
    patch: {
      haloColor: 'rgba(220, 224, 232, 0.6)',
      regionColors: { skin: '#d7dbe2', hair: '#aeb4c0', beard: '#9aa0ac', iris: '#eef1f5', lips: '#c4cad4', noseTint: '#cfd4dc', brows: '#8d93a0' },
      colorOpts: { iris: 0.3, lips: 0.3, nose: 0.2, brow: 0.4 },
    },
  },
];

export function mergeConfig(partial: Partial<AvatarConfig> | null | undefined): AvatarConfig {
  if (!partial) return structuredClone(DEFAULT_AVATAR_CONFIG);
  return {
    ...DEFAULT_AVATAR_CONFIG,
    ...partial,
    regionColors: { ...DEFAULT_AVATAR_CONFIG.regionColors, ...(partial.regionColors ?? {}) },
    regionDensity: { ...DEFAULT_AVATAR_CONFIG.regionDensity, ...(partial.regionDensity ?? {}) },
    colorOpts: { ...DEFAULT_AVATAR_CONFIG.colorOpts, ...(partial.colorOpts ?? {}) },
    stateSpeeds: { ...DEFAULT_AVATAR_CONFIG.stateSpeeds, ...(partial.stateSpeeds ?? {}) },
    stateFreqs: { ...DEFAULT_AVATAR_CONFIG.stateFreqs, ...(partial.stateFreqs ?? {}) },
  };
}

export interface AvatarPhoto {
  id: string; fileName: string; mimeType: string; sizeBytes: number;
  width: number | null; height: number | null; isActive: boolean;
  createdAt: number; url: string;
  config: Partial<AvatarConfig> | null; // each photo keeps its own complete tuning
}

// ── Particle field ───────────────────────────────────────────────────────────

export interface Particle {
  tx: number; ty: number; lum: number; color: string | null;
  phase: number; speed: number; dr: number; ox: number; oy: number;
  formDelay: number; mouthWeight: number; featureWeight: number;
  region: string; isTeeth: boolean; halo: boolean; rng: number;
  cx: number; cy: number;
}
export interface ParticleField { particles: Particle[]; aspect: number }

function hexToRgb(h: string | undefined): [number, number, number] | null {
  if (!h) return null;
  const m = /^#?([0-9a-f]{6})$/i.exec(h.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// Default heuristic region ellipses (normalized), used when no detected
// faceRegions are present.
export const DEFAULT_FACE_REGIONS: FaceRegions = {
  eyeL: { x: 0.36, y: 0.43, rx: 0.07, ry: 0.04 },
  eyeR: { x: 0.66, y: 0.43, rx: 0.07, ry: 0.04 },
  browL: { x: 0.36, y: 0.38, rx: 0.10, ry: 0.03 },
  browR: { x: 0.66, y: 0.38, rx: 0.10, ry: 0.03 },
  nose: { x: 0.51, y: 0.55, rx: 0.07, ry: 0.10 },
  mouth: { x: 0.51, y: 0.76, rx: 0.16, ry: 0.05 },
  beardL: { x: 0.20, y: 0.82, rx: 0.12, ry: 0.12 },
  beardR: { x: 0.82, y: 0.82, rx: 0.12, ry: 0.12 },
};
const F = DEFAULT_FACE_REGIONS;
export const inEllipse = (nx: number, ny: number, e: Ellipse) => {
  const dx = (nx - e.x) / e.rx, dy = (ny - e.y) / e.ry;
  return dx * dx + dy * dy < 1;
};

// Which region a normalized (nx,ny) point falls in — used by click-to-paint and
// the sampler. Honors detected faceRegions when given.
export function regionAt(nx: number, ny: number, regions?: FaceRegions): string {
  const RG = regions ?? DEFAULT_FACE_REGIONS;
  if (inEllipse(nx, ny, RG.eyeL) || inEllipse(nx, ny, RG.eyeR)) return 'eyes';
  if (inEllipse(nx, ny, RG.browL) || inEllipse(nx, ny, RG.browR)) return 'brows';
  if (inEllipse(nx, ny, RG.mouth)) return 'mouth';
  if (inEllipse(nx, ny, RG.nose)) return 'nose';
  if (ny < 0.20) return 'hair';
  if (ny > 0.85 || inEllipse(nx, ny, RG.beardL) || inEllipse(nx, ny, RG.beardR)) return 'beard';
  return 'skin';
}

/**
 * Samples an already-decoded HTMLImageElement into a particle field, applying
 * per-region colors and tint intensities from config.
 */
export function sampleImageField(img: HTMLImageElement, config: AvatarConfig): ParticleField {
  const W = config.sampleW;
  const H = Math.round((img.height / img.width) * W);
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, W, H);
  const data = ctx.getImageData(0, 0, W, H).data;

  const AMBER: [number, number, number] = [255, 200, 130];
  const rc = config.regionColors;
  const co = config.colorOpts;
  const skinBase = hexToRgb(rc.skin) || AMBER;
  const hairBase = hexToRgb(rc.hair) || skinBase;
  const beardBase = hexToRgb(rc.beard) || skinBase;
  const irisTgt = hexToRgb(rc.iris) || [110, 160, 230];
  const lipsTgt = hexToRgb(rc.lips) || [255, 140, 140];
  const noseTgt = hexToRgb(rc.noseTint) || [255, 170, 130];
  const browTgt = hexToRgb(rc.brows) || [180, 130, 90];
  const mix = (a: number[], b: number[], t: number): [number, number, number] => [
    Math.round(a[0]! * (1 - t) + b[0]! * t),
    Math.round(a[1]! * (1 - t) + b[1]! * t),
    Math.round(a[2]! * (1 - t) + b[2]! * t),
  ];

  // Use landmark-detected regions when available, else the fixed heuristics.
  const RG = config.faceRegions ?? F;
  const regionOf = (nx: number, ny: number): string => {
    if (inEllipse(nx, ny, RG.eyeL) || inEllipse(nx, ny, RG.eyeR)) return 'eyes';
    if (inEllipse(nx, ny, RG.browL) || inEllipse(nx, ny, RG.browR)) return 'brows';
    if (inEllipse(nx, ny, RG.mouth)) return 'mouth';
    if (inEllipse(nx, ny, RG.nose)) return 'nose';
    if (ny < 0.20) return 'hair';
    if (ny > 0.85 || inEllipse(nx, ny, RG.beardL) || inEllipse(nx, ny, RG.beardR)) return 'beard';
    return 'skin';
  };

  // Resemblance: blend the stylized region color toward the REAL sampled pixel
  // by photoColorMix (0 = pure palette, 1 = the photo's actual colors). This is
  // what makes the dot cloud actually look like the person.
  const pcm = Math.max(0, Math.min(1, config.photoColorMix ?? 0));
  const colorize = (r: number, g: number, b: number, lum: number, region: string): [number, number, number] => {
    let base: [number, number, number];
    if (region === 'hair') base = hairBase;
    else if (region === 'beard') base = beardBase;
    else if (region === 'eyes') base = (lum < 0.50 && lum > 0.08) ? mix(skinBase, irisTgt, 0.55 * co.iris) : skinBase;
    else if (region === 'mouth') base = (lum < 0.65 && lum > 0.15) ? mix(skinBase, lipsTgt, 0.35 * co.lips) : skinBase;
    else if (region === 'nose' && lum > 0.45 && lum < 0.80) base = mix(skinBase, noseTgt, 0.20 * co.nose);
    else if (region === 'brows' && lum < 0.45) base = mix(skinBase, browTgt, 0.40 * co.brow);
    else base = skinBase;
    return pcm <= 0 ? base : mix(base, [r, g, b], pcm);
  };

  // Optional head mask — keep only pixels inside a head-shaped ellipse so the
  // photo's background is dropped. Derived from the detected face when present.
  let head: Ellipse | null = null;
  if (config.cropToHead) {
    if (config.faceRegions) {
      const cx = (RG.eyeL.x + RG.eyeR.x) / 2;
      const browY = Math.min(RG.browL.y, RG.browR.y);
      const span = Math.max(0.15, RG.mouth.y - browY);
      const top = Math.max(0, browY - span * 1.0);          // forehead + hair
      const bottom = Math.min(1, RG.mouth.y + span * 0.85);  // chin
      const halfW = Math.max(0.18, (RG.eyeR.x - RG.eyeL.x) * 1.45); // cheeks/ears
      head = { x: cx, y: (top + bottom) / 2, rx: halfW, ry: (bottom - top) / 2 };
    } else {
      head = { x: 0.5, y: 0.48, rx: 0.42, ry: 0.52 };
    }
  }

  const particles: Particle[] = [];
  const step = 2;
  // Higher fidelity → tighter, grid-aligned dots that keep more detail, so a
  // real photo reads as a coherent image instead of random spray.
  const fid = Math.max(0, Math.min(1, config.fidelity));
  const darkDrop = 0.35 * (1 - fid * 0.8); // keep shadows/hair as fidelity rises
  const jitterAmt = step * 0.6 * (1 - fid * 0.9); // ~grid-aligned at high fidelity
  const considerPixel = (x: number, y: number) => {
    const i = (y * W + x) * 4;
    const r = data[i]!, g = data[i + 1]!, b = data[i + 2]!;
    const isBg = (b > r + 8) && (b > g + 4) && (b > 80);
    if (isBg) return;
    const rawLum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    if (rawLum < 0.14 && Math.random() < darkDrop) return;
    const nx = x / W, ny = y / H;
    if (head && !inEllipse(nx, ny, head)) return; // drop background outside the head
    const region = regionOf(nx, ny);
    const lum = rawLum > 0.78 ? 0.55 + (rawLum - 0.78) * 0.2 : rawLum;
    const [pr, pg, pb] = colorize(r, g, b, rawLum, region);
    const isTeeth = region === 'mouth' && rawLum > 0.65;
    const jitter = jitterAmt;
    const oAng = Math.random() * Math.PI * 2;
    const oRad = 1.6 + Math.random() * 2.0;
    particles.push({
      tx: (x + (Math.random() - 0.5) * jitter) / W,
      ty: (y + (Math.random() - 0.5) * jitter) / H,
      lum, color: `rgb(${pr},${pg},${pb})`,
      phase: Math.random() * Math.PI * 2,
      speed: 0.4 + Math.random() * 0.8,
      dr: 0.6 + Math.random() * 1.4,
      ox: 0.5 + Math.cos(oAng) * oRad,
      oy: 0.5 + Math.sin(oAng) * oRad,
      formDelay: Math.random() * 0.35,
      mouthWeight: 0, featureWeight: 0,
      region, isTeeth, halo: false, rng: Math.random(),
      cx: 0, cy: 0,
    });
  };

  for (let y = 0; y < H; y += step) for (let x = 0; x < W; x += step) considerPixel(x, y);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (y % step === 0 && x % step === 0) continue;
    const reg = regionOf(x / W, y / H);
    if (reg !== 'eyes' && reg !== 'brows' && reg !== 'nose' && reg !== 'mouth') continue;
    considerPixel(x, y);
  }

  const mc = RG.mouth;
  for (const p of particles) {
    const dy = p.ty - mc.y, dx = p.tx - mc.x;
    const d = Math.sqrt(dx * dx * 2.2 + dy * dy);
    p.mouthWeight = Math.max(0, 1 - d * 4.5);
  }
  const FW = [
    { ...RG.eyeL, w: 1.7 },
    { ...RG.eyeR, w: 1.7 },
    { ...RG.browL, w: 1.0 },
    { ...RG.browR, w: 1.0 },
    { ...RG.nose, w: 0.9 },
    { ...RG.mouth, w: 1.6 },
  ];
  for (const p of particles) {
    let fw = 0;
    for (const f of FW) {
      const dx = (p.tx - f.x) / f.rx, dy = (p.ty - f.y) / f.ry;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < 1) fw = Math.max(fw, f.w * (1 - d));
    }
    p.featureWeight = fw;
  }

  // Halo
  const haloCount = 160;
  for (let i = 0; i < haloCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const r2 = 0.55 + Math.random() * 0.35;
    const oAng = Math.random() * Math.PI * 2;
    const oRad = 1.8 + Math.random() * 2.4;
    particles.push({
      tx: 0.5 + Math.cos(a) * r2 * 0.55,
      ty: 0.5 + Math.sin(a) * r2 * 0.7,
      lum: 0.2 + Math.random() * 0.4, color: null,
      phase: Math.random() * Math.PI * 2, speed: 0.2 + Math.random() * 0.5,
      dr: 4 + Math.random() * 8,
      ox: 0.5 + Math.cos(oAng) * oRad, oy: 0.5 + Math.sin(oAng) * oRad,
      formDelay: Math.random() * 0.55, mouthWeight: 0, featureWeight: 0,
      region: 'halo', isTeeth: false, halo: true, rng: Math.random(),
      cx: 0, cy: 0,
    });
  }

  return { particles, aspect: W / H };
}

// Auto-tune region colors directly from the active photo: averages the pixels
// inside each facial region (skin/hair/beard/eyes/lips/nose/brows) and writes
// them back as the region palette. A dependency-free stand-in for full landmark
// detection — one click makes the dot cloud match the portrait's real colors.
function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}
export function autoTuneFromPhoto(img: HTMLImageElement, config: AvatarConfig): Partial<AvatarConfig> {
  const W = Math.min(220, config.sampleW);
  const H = Math.round((img.height / img.width) * W);
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return {};
  ctx.drawImage(img, 0, 0, W, H);
  const data = ctx.getImageData(0, 0, W, H).data;

  type Acc = { r: number; g: number; b: number; n: number };
  const acc: Record<string, Acc> = {};
  const add = (k: string, r: number, g: number, b: number) => {
    const a = acc[k] ?? (acc[k] = { r: 0, g: 0, b: 0, n: 0 });
    a.r += r; a.g += g; a.b += b; a.n++;
  };
  // Use landmark-detected regions when available, else the fixed heuristics.
  const RG = config.faceRegions ?? F;
  const regionOf = (nx: number, ny: number): string => {
    if (inEllipse(nx, ny, RG.eyeL) || inEllipse(nx, ny, RG.eyeR)) return 'eyes';
    if (inEllipse(nx, ny, RG.browL) || inEllipse(nx, ny, RG.browR)) return 'brows';
    if (inEllipse(nx, ny, RG.mouth)) return 'mouth';
    if (inEllipse(nx, ny, RG.nose)) return 'nose';
    if (ny < 0.20) return 'hair';
    if (ny > 0.85 || inEllipse(nx, ny, RG.beardL) || inEllipse(nx, ny, RG.beardR)) return 'beard';
    return 'skin';
  };
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = (y * W + x) * 4;
    const r = data[i]!, g = data[i + 1]!, b = data[i + 2]!;
    if ((b > r + 8) && (b > g + 4) && (b > 80)) continue; // skip blue-ish background
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    if (lum < 0.06) continue;
    const reg = regionOf(x / W, y / H);
    // For eyes/lips, bias toward the saturated (non-skin) pixels so iris/lip
    // color is picked rather than surrounding skin.
    if (reg === 'eyes' && !(lum < 0.5 && lum > 0.08)) continue;
    if (reg === 'mouth' && !(lum < 0.7 && lum > 0.15)) continue;
    add(reg, r, g, b);
  }
  const avg = (k: string, fallback: string): string => {
    const a = acc[k];
    if (!a || a.n < 8) return fallback;
    return rgbToHex(a.r / a.n, a.g / a.n, a.b / a.n);
  };
  const base = config.regionColors;
  const skin = avg('skin', base.skin);
  const regionColors: RegionColors = {
    skin,
    hair: avg('hair', base.hair),
    beard: avg('beard', base.beard),
    iris: avg('eyes', base.iris),
    lips: avg('mouth', base.lips),
    noseTint: avg('nose', base.noseTint),
    brows: avg('brows', base.brows),
  };
  const sr = hexToRgb(skin) ?? [255, 200, 130];
  return {
    regionColors,
    haloColor: `rgba(${sr[0]}, ${sr[1]}, ${sr[2]}, 0.6)`,
  };
}

/** Authenticated image fetch → object URL → decoded HTMLImageElement (same-origin, untainted). */
export async function loadAuthedImage(url: string): Promise<HTMLImageElement> {
  const token = localStorage.getItem('mdai_token');
  const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}${url}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error(`image fetch ${url}: ${res.status}`);
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('decode failed'));
      img.src = objUrl;
    });
    return img;
  } finally {
    // Revoke on next tick — the Image keeps its own decoded copy.
    setTimeout(() => URL.revokeObjectURL(objUrl), 0);
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAvatar() {
  const configQ = useQuery<{ config: Partial<AvatarConfig> | null }>({
    queryKey: ['avatar', 'config'],
    queryFn: () => api('/api/avatar/config'),
  });
  const photosQ = useQuery<{ photos: AvatarPhoto[] }>({
    queryKey: ['avatar', 'photos'],
    queryFn: () => api('/api/avatar/photos'),
  });
  const globalConfig = mergeConfig(configQ.data?.config ?? null);
  const photos = photosQ.data?.photos ?? [];
  const activePhoto = photos.find((p) => p.isActive) ?? null;
  // The active photo's own config wins; otherwise the legacy global config.
  const config = activePhoto?.config ? mergeConfig(activePhoto.config) : globalConfig;
  return { config, globalConfig, photos, activePhoto, loading: configQ.isLoading || photosQ.isLoading };
}

// The avatar to RENDER for the current user — org-aware (professional layer): a
// colleague gets their org's avatar; everyone else the global active one.
export function useEffectiveAvatar(): { photoUrl: string | null; config: AvatarConfig; loading: boolean } {
  const q = useQuery<{ photoUrl: string | null; config: Partial<AvatarConfig> | null }>({
    queryKey: ['avatar', 'effective'],
    queryFn: () => api('/api/avatar/effective'),
  });
  return { photoUrl: q.data?.photoUrl ?? null, config: mergeConfig(q.data?.config ?? null), loading: q.isLoading };
}
